import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { isPlatformAdmin } from '../utils/auth';
import LoginBrandPanel from '../components/auth/LoginBrandPanel';
import LoginForm from '../components/auth/LoginForm';

/**
 * Login — split-pane analytics-grade auth screen.
 *
 * Left:  LoginBrandPanel  — animated dark-navy "power-grid" brand pane
 *                            (sinewave + drifting orbs + dot grid + HD
 *                            logo + rotating stat badges).
 * Right: LoginForm        — the original email/password fields + auth
 *                            wiring, re-skinned with a phase-driven
 *                            ProgressButton and a slide-in error banner.
 *
 * The form panel owns its inputs; this page owns the submit state
 * machine ('idle' → 'submitting' → 'finalizing' → success/error) and
 * the navigate-after-success step. The auth contract (useAuth().login)
 * is unchanged from the previous version of this file.
 */
const FINALIZING_DELAY_MS = 350;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [submitState, setSubmitState] = useState({ phase: 'idle', error: '' });
  const finalizingTimer = useRef(null);

  const handleSubmit = useCallback(
    async ({ email, password }) => {
      const trimmedEmail = String(email || '').trim();
      const trimmedPassword = String(password || '').trim();
      setSubmitState({ phase: 'submitting', error: '' });
      try {
        await login(trimmedEmail, trimmedPassword);

        // Brief 'finalizing' phase so the progress chrome reads as a
        // two-step journey (auth → load dashboard) rather than a
        // single instantaneous flash. Keeps the perceived premium
        // feel without slowing the user down materially.
        finalizingTimer.current = window.setTimeout(() => {
          setSubmitState({ phase: 'finalizing', error: '' });
        }, FINALIZING_DELAY_MS);

        toast.success('Welcome back!');

        const raw = localStorage.getItem('txnweb_user');
        let next = '/dashboard';
        try {
          const u = raw ? JSON.parse(raw) : null;
          if (isPlatformAdmin(u)) next = '/admin/dashboard';
        } catch {
          /* ignore */
        }

        setSubmitState({ phase: 'success', error: '' });
        navigate(next);
      } catch (err) {
        if (finalizingTimer.current) {
          window.clearTimeout(finalizingTimer.current);
          finalizingTimer.current = null;
        }
        const message = err?.message || 'Sign in failed. Please try again.';
        setSubmitState({ phase: 'error', error: message });
        toast.error(message);
      }
    },
    [login, navigate],
  );

  return (
    <div className="txnweb-login-shell">
      <LoginBrandPanel />
      <LoginForm onSubmit={handleSubmit} submitState={submitState} />
    </div>
  );
}
